import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

export interface CriticalUpdateDialogData {
  storeUrl: string;
  onOpenStore?: () => Promise<void>;
}

@Component({
  selector: 'app-critical-update-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatProgressSpinnerModule
  ],
  template: `
    <div class="popup-outer">
      <div class="popup-container">
        <div class="popup-top">
          <div class="popup-header">
            <span class="confirm-header">Update Required</span>
          </div>
          <div class="popup-exit">
            <!-- No dismiss button for critical update -->
          </div>
        </div>
        <div class="popup-bottom">
          <div class="confirm-outer">
            <div class="confirm-body">
              A newer version of this app is available. Please update to continue.
            </div>
            <div class="confirm-buttons">
              <button type="button" class="btn btn-primary" (click)="openStore()" [disabled]="isOpening">
                {{isOpening ? 'Opening...' : 'Open Store'}}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Overlay with spinner when opening store -->
      @if (isOpening) {
        <div class="loading-overlay">
          <mat-spinner diameter="50"></mat-spinner>
        </div>
      }
    </div>
  `,
  styles: [`
    .popup-outer {
      background-color: white;
      border-radius: 20px;
      border: 1px solid #cccccc;
      width: 100%;
    }

    .popup-top {
      background-color: #d9d9d9;
      display: flex;
      flex-direction: row;
      border-radius: 20px 20px 0 0;
      padding: 12px;
    }

    .popup-header {
      flex-grow: 1;
      justify-content: center;
    }

    .popup-exit {
      width: 35px;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .popup-bottom {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 12px;
      border-radius: 0 0 20px 20px;
    }

    .confirm-header {
      font-size: 19px;
    }

    .confirm-outer {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .confirm-body {
      text-align: center;
      padding: 15px;
    }

    .confirm-buttons {
      display: flex;
      flex-direction: row;
      gap: 10px;
      justify-content: center;
    }

    .loading-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(255, 255, 255, 0.9);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      border-radius: 20px;
    }

    button:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  `]
})
export class CriticalUpdateDialogComponent {
  isOpening = false;

  constructor(
    public dialogRef: MatDialogRef<CriticalUpdateDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CriticalUpdateDialogData
  ) {}

  async openStore(): Promise<void> {
    this.isOpening = true;

    try {
      if (this.data.onOpenStore) {
        await this.data.onOpenStore();
      }
      // Close on success
      this.dialogRef.close(true);
    } catch (error) {
      // On error, hide spinner and keep modal open
      console.error('Failed to open store:', error);
      this.isOpening = false;
    }
  }
}
